// ═══════════════════════════════════════════════════════════════════════════════════════════
// AgentDrawer — level 2 (or level 1) drawer body for a single recipient
// (CONTRACT §4.2, `SendSearch-Mock-v3.html:359-380`).
//
// DELIVERY PATH: _delivery\SendSearch-V1\react\screens\SendSearch\components\AgentDrawer.tsx
// TARGET PATH:   ReactCode\src\screens\SendSearch\components\AgentDrawer.tsx
//
// The mock's card order is kept: verdict → channel lines → send details → provenance → values.
// The verdict is a SENTENCE, not a status chip: the whole point of the drawer is that an operator can
// read one line to a client on the phone.
//
// PER-RECIPIENT VALUES — a reversal, recorded here because this comment used to say the opposite.
// V1 said: "V1 does not store per-recipient as-sent values (CONTRACT D5/§9)", so no such card.
// That was true of a REBUILD from template + data — which is what the mock's "המייל שנשלח" panel
// (`:394-404`) is, and which would have been an invented email presented as a record.
// It was NOT true of the token VALUES: `dbo.DataSources_GetRowValuesForPreviewByClient` reads the
// row out of the LOCKED source version that was actually sent, so the values are read, not
// reconstructed. Idan reversed the decision on 2026-08-03 and the fourth card below is the result.
// The distinction the old comment was defending still holds, and still holds here:
//   • the values are shown because they were RECORDED;
//   • `HasRow = false` means there was no source row for this client, i.e. the sender emitted empty
//     strings — so the card says that IN WORDS and renders no rows at all. Printing the blank
//     values would read as "this recipient was deliberately sent empty text", which is precisely
//     the confident over-claim this header exists to forbid.
//
// THE PREVIEW BUTTON — a second reversal, and the note above it used to forbid it.
// The old rule ("no preview button, no token minted from this screen") was defending against a
// CLIENT-SIDE REBUILD of the mail: template + data merged in the browser is a second rendering
// engine that knows less than the sender's and drifts from it. That objection is intact and is
// exactly why the button does NOT do that. It opens an IFRAME over `PreviewCampaign.aspx` — the
// same page that rendered the mail — using an encrypted id the SERVER minted onto the row as
// `PreviewUrl` (CONTRACT §1, LEDGER #1). This screen still mints no token, still merges nothing.
//   🔴 `PreviewUrl == null` ⇒ the button is DISABLED with a tooltip that says why. Null is the
//   server's answer for a non-email channel, a missing ChannelCampaignID, and — the one that
//   matters — `ClientID <= 0` (§1.1): an id carrying a CampaignID without a clientid does not
//   throw, it renders a GENERIC campaign that looks entirely plausible and is not this agent's
//   mail. `ClientID` is 0 until deploy script 23 runs, so the button ships disabled. That is the
//   CORRECT behaviour, not a bug.
//
// What is STILL deliberately NOT here:
//   • no percentages (the denominator differs per channel — `:443`);
//   • no reconstructed message body. The message drawer level is opened only when the caller says
//     a stored message exists.
//
// 11-sendsearch §8 — ONE CARD PER CHANNEL. A report row is now one person in one SEND, so the old
// single verdict card (which chose the "strongest evidence" across two channels) is split: an
// e-mail card (today's verdict + "צפה במייל שנשלח") when the send has e-mail, and a WhatsApp card
// (unit, template, number, sent → delivered → read, error, "צפייה בהודעה") when it has WhatsApp.
// Each card speaks ONLY its channel's states — a WhatsApp read can no longer be summarised under an
// e-mail headline or the other way round. "צפייה בהודעה" opens WaSentMessageDialog: the phone with
// the recorded values and the template text as stored today, which the dialog says in words.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@material-ui/core';
import { MailOutline, Visibility } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DateFormats } from '../../../helpers/Constants';
import InlineBanner from '../../SmartSend/components/InlineBanner';
import {
    SS,
    SendSearchRow,
    SendProvenanceRow,
    SendRowValue,
    StateTone,
    ChannelAttempt,
    camelCaseState,
    channelFilterKey,
    deliveryTone,
    eSendSearchChannel,
    engagementTone,
    isProvenanceSource,
    rowEmailAttempt,
    rowHasEmailAttempt,
    rowWhatsAppAttempt,
    rowWhatsAppNumberOf,
    waSubLineOf,
} from '../../../Models/DataSources/SendSearch';
import VersionBadge from './VersionBadge';
import EmailPreviewDialog from './EmailPreviewDialog';
import WaSentMessageDialog from './WaSentMessageDialog';
import { previewUrlOf } from './SendSearchAdvanced';
import { formatValue } from '../../DataSources/components/formatValue';
import { eDataType } from '../../../Models/DataSources/DataSourceEnums';

// QA BUG-02: the Tooltips of this drawer render their popper INSIDE the drawer paper. Portaled to <body>
// (MUI's default) the popper sits under the drawer: src/index.css:129-131 pins every `.MuiTooltip-popper`
// to `z-index: 100 !important` (global, deliberately left as is) and the MUI v4 Drawer is a Modal at
// 1300. Same idiom as SummaryModal.tsx / AIFloatingButton.tsx. Module-level: one identity across renders.
const IN_DRAWER_POPPER = { disablePortal: true };

const TONE_COLOR: { [k in StateTone]: string } = {
    ok: '#067647', bad: '#B42318', warn: '#B54708', muted: '#5b6b7b',
};

interface Props {
    row: SendSearchRow;
    provenance: SendProvenanceRow[];
    provenanceLoading: boolean;
    // Set when the Provenance fetch FAILED. A failed fetch and a genuinely empty history both arrive
    // as `provenance: []`, and the empty branch below asserts something positive about the data
    // ("the mapping was not touched, so this IS the version that was sent"). Asserting that from a
    // request that never returned is the confident-lie failure mode, so the two cases are separated.
    provenanceError?: string | null;
    // ── the values this recipient actually received (GET api/SendSearch/RowValues) ───────────
    // Three props, not one, for the same reason provenance needs three: an empty list means
    // something ("the campaign has no token mapping"), a failed fetch means something else
    // ("we do not know"), and neither may be rendered as the other.
    rowValues?: SendRowValue[];
    rowValuesLoading?: boolean;
    rowValuesError?: string | null;
    // The ClientID the loaded values belong to. The store holds ONE slot; if it is not this
    // recipient's, the list is not rendered at all — see `valuesUnavailable` below.
    rowValuesClientId?: number | null;
    // 11-sendsearch §8: the app-wide sheet, handed to WaSentMessageDialog's phone.
    appClasses?: any;
}

const Card: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box style={{ background: 'var(--pls-d-surface, #fff)', border: '1px solid var(--pls-d-border, #e0e0e0)', borderRadius: 10, padding: 17 }}>
        {title && (
            <Typography
                component="div"
                style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.09em', color: 'var(--pls-d-text-2, #5b6b7b)', textTransform: 'uppercase', marginBottom: 11 }}
            >
                {title}
            </Typography>
        )}
        {children}
    </Box>
);

const Kv: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--pls-d-border, #eef1f5)', fontSize: 14 }}>
        <Typography component="span" style={{ color: 'var(--pls-d-text-2, #5b6b7b)', fontSize: 14 }}>{label}</Typography>
        <Box style={{ fontWeight: 700 }}>{children}</Box>
    </Box>
);

const AgentDrawer: React.FC<Props> = ({
    row, provenance, provenanceLoading, provenanceError,
    rowValues, rowValuesLoading, rowValuesError, rowValuesClientId, appClasses,
}) => {
    const { t } = useTranslation();

    // The ONE gate in front of the iframe. `previewUrlOf` returns null for absent, empty, and
    // non-http(s) — see its comment: an `<iframe src="">` loads the HOSTING page inside itself, so
    // the operator would be looking at the report nested in the report and reading it as the email.
    const previewUrl = previewUrlOf(row);
    const [previewOpen, setPreviewOpen] = useState(false);
    // 11-sendsearch §8: the WhatsApp message viewer. Enabled only when there IS a message row —
    // `WaMessageID > 0` — so the dialog never asks the server for a message that does not exist.
    const waMessageId = row.WaMessageID ?? 0;
    const [waOpen, setWaOpen] = useState(false);
    const waNumber = rowWhatsAppNumberOf(row);

    const values: SendRowValue[] = rowValues ?? [];
    // The recipient has a source row in the sent version. `HasRow` is a per-CLIENT fact that the SP
    // repeats on every returned row, so `some` and `every` agree in practice; `some` is used because
    // the failure it guards against is rendering blanks, and one real row is enough to justify the
    // table. FALSE (or an empty list) ⇒ the sender emitted empty strings for every token, and the
    // card must say so in words instead of printing a column of blanks.
    const hasSourceRow = values.some((v) => v.HasRow);
    // The row-values fetch is keyed on ClientID. A row that arrived without one — an older server
    // that does not yet project it (B.2) — means the fetch was never dispatched, which is "we did
    // not ask", NOT "the recipient received nothing". Rendering the no-values sentence for it would
    // assert the second from the first, so it degrades to the load-failure line instead: the
    // conservative end, claiming nothing about the data.
    //
    // The second half is the anti-staleness guard: the slice holds ONE values slot, so a slot that
    // belongs to a different ClientID must not be rendered under this name. In practice the two
    // dispatches (push the drawer, fetch the values) happen in the same handler, so the id is
    // already this recipient's by first paint; the guard is for every path that does not fetch.
    const valuesUnavailable = !row.ClientID || row.ClientID <= 0 || rowValuesClientId !== row.ClientID;

    // 11-sendsearch §8 — one card per channel, each built from its OWN narrowed attempt. `null` ⇒
    // the send has no such channel ⇒ no card. These are the same NARROWED values the grid's two
    // columns render (`rowEmailAttempt` / `rowWhatsAppAttempt` → `toChannelAttempt`), so the drawer
    // and the row it was opened from can never disagree about a state (D10). The old guard's
    // null-blindness cannot come back: the helpers never hand a NULL to `camelCaseState`.
    const emailAttempt: ChannelAttempt | null = rowEmailAttempt(row);
    const waAttempt: ChannelAttempt | null = rowWhatsAppAttempt(row);
    // The person HAS an e-mail attempt — gates the e-mail times and the values card (§8). On a row
    // without one, `SentAt` is the WhatsApp send time (§3 col 16) and must not be printed as e-mail.
    const hasEmailAttempt = rowHasEmailAttempt(row);

    // A channel's headline: its strongest evidence in words — a real engagement outranks the
    // delivery state — and the matching tone. Key construction only; no status table.
    const headlineOf = (at: ChannelAttempt): { text: string; tone: StateTone; engaged: boolean } => {
        const engaged = at.EngagementState !== 'None';
        return {
            text: engaged
                ? t(`${SS}engagement.${camelCaseState(at.EngagementState)}`)
                : t(`${SS}delivery.${camelCaseState(at.DeliveryState)}`),
            tone: engaged ? engagementTone(at.EngagementState) : deliveryTone(at.DeliveryState),
            engaged,
        };
    };
    const emailHead = emailAttempt ? headlineOf(emailAttempt) : null;
    const waHead = waAttempt ? headlineOf(waAttempt) : null;
    const hasEngagement = !!emailHead && emailHead.engaged;
    // The WhatsApp reason / error line — decided in the model (`waSubLineOf`), worded here.
    const waSub = waSubLineOf(row);
    const fmtAt = (iso: string | null | undefined): string => (iso ? moment(iso).format(DateFormats.DATE_TIME_24) : '—');

    // ── which provenance row this recipient's version actually came from, or null ─────────────────
    // Added 2026-08-13 (owner request): put the source NAME and the DATE up beside V1 in the version
    // row, so the separate "איזו גרסה נשלחה" card below stops repeating them. This const is the ONLY
    // condition under which that card is suppressed.
    //
    // 🔴 NOT `provenance.length === 1` on its own. That is TOP-1 attribution, which
    // RollupDrawer.tsx:131-142 forbids in writing. The list is fetched per CAMPAIGN + CHANNEL
    // (SendSearchPanel.tsx:350) and carries no ClientID (SendProvenanceRow, CONTRACT §3.1), so a lone
    // row can legitimately belong to a LATER send than this recipient's — a campaign sent before the
    // provenance table existed and re-sent after it produces exactly that shape. Printing that row's
    // name and date beside this person's V1 while ALSO deleting the card that would have exposed the
    // mismatch is precisely the confident-lie failure mode this file's header exists to forbid.
    //
    // `row.ProvenanceSource === 'Recorded'` is the SERVER's own statement (CONTRACT §2.2 precedence)
    // that this row's version was resolved FROM a provenance row of this campaign+channel. With
    // exactly one such row in scope, that row is the one the server matched. Every other shape —
    // zero rows, more than one, a failed fetch, still loading, or a row graded Inferred/Unverifiable —
    // leaves the card standing and unchanged.
    const attributed: SendProvenanceRow | null = (!provenanceLoading && !provenanceError
        && provenance.length === 1 && row.ProvenanceSource === 'Recorded')
        ? provenance[0]
        : null;

    return (
        <>
            {/* Mounted only once opened, so a drawer that is never asked for a preview never loads
                the campaign page — one request per DELIBERATE view, not one per opened row. */}
            {previewOpen && (
                <EmailPreviewDialog
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    url={previewUrl}
                    recipientName={row.RecipientName}
                    recipientEmail={row.RecipientEmail}
                    sentAt={row.SentAt}
                    VersionNumber={row.VersionNumber}
                    ProvenanceSource={row.ProvenanceSource}
                    VersionState={row.VersionState}
                />
            )}

            {/* The WhatsApp message viewer — mounted only once asked for, same rule as the e-mail one. */}
            {waOpen && waMessageId > 0 && (
                <WaSentMessageDialog
                    open={waOpen}
                    onClose={() => setWaOpen(false)}
                    messageId={waMessageId}
                    recipientName={row.RecipientName}
                    appClasses={appClasses}
                />
            )}

            {/* HasRow = false: no row for this person in the source version the send went out from.
                Stated at the TOP, before the channel cards, because it changes what they mean.
                11-sendsearch §8: its OWN sentence. It used to borrow "לא נשלח בשום ערוץ" and the
                "mapping was changed" tooltip — both false here: a row with NO source row exists only when
                the person has a real attempt (§0), so something WAS sent, and the mapping may be untouched.
                (A row for a person sent nothing in any channel — QA BUG-18 — is built FROM its source row,
                so it never reaches this banner.)
                What is true is narrower: no source row ⇒ no source values or version for this person. */}
            {!row.HasRow && (
                <InlineBanner
                    severity="warning"
                    role="status"
                    title={t(`${SS}drawer.noSourceRowTitle`)}
                    body={t(`${SS}drawer.noSourceRowBody`)}
                />
            )}

            {/* AG-10 «לא נשלח בשום ערוץ»: a person in the send who got nothing in any channel (QA BUG-18 —
                HasEmail === false AND HasWhatsApp === false; the channel cards below still say why per
                channel), or a malformed row with neither channel — said in words rather than an empty
                drawer. An older server never sends 0/0 (every row has an attempt), so it shows as before. */}
            {((!emailHead && !waHead) || (row.HasEmail === false && row.HasWhatsApp === false)) && (
                <Card>
                    <Typography component="div" style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.3, color: TONE_COLOR.muted }}>
                        {t(`${SS}empty.notSent`)}
                    </Typography>
                </Card>
            )}

            {/* ── the E-MAIL card — today's verdict card, scoped to the e-mail channel ── */}
            {emailHead && (
            <Card title={t(channelFilterKey(eSendSearchChannel.EMAIL))}>
                <Typography component="div" style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.3, color: TONE_COLOR[emailHead.tone] }}>
                    {emailHead.text}
                </Typography>
                {/* THE TWO FACTS, LABELLED, plus the action — moved here 2026-08-13 (owner request).
                    What stood here was ONE UNLABELLED timestamp (`hasEngagement ? EngagementAt :
                    SentAt`), so under a "נכשל" headline it read as "the time it failed" — a value the
                    server does not record: §3.2 gives this row `SentAt` and `EngagementAt`, nothing
                    else. The label is what fixes that, and it is what lets the send time live here
                    instead of 50px lower in a second card.
                    `col.sent`, not the literal "אימייל נשלח": it is the grid's own header for the same
                    value. The channel is named by the card's own title (11-sendsearch §8).
                    NO `direction` and NO `textAlign` here. The `textAlign:'right'` that stood on this
                    block was a live en/pl defect: inline styles bypass jss-rtl (the bug
                    SendStatusCell.tsx:119-124 records), so a physical 'right' pinned the line to the
                    END edge under a LTR locale. Each timestamp is isolated on its own instead, and
                    `unicodeBidi:'isolate'` is load-bearing rather than decoration — `direction:'ltr'`
                    alone on an inline span is inert, and 'DD/MM/YYYY HH:mm' then renders as
                    "10:04 09/08/2026". */}
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 5 }}>
                    {/* 11-sendsearch §8: only when the person HAS an e-mail attempt — otherwise
                        `SentAt` is the WhatsApp send time and would date an e-mail never sent. */}
                    {hasEmailAttempt && (
                        <Typography component="div" style={{ fontSize: 14, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                            <Typography component="span" style={{ whiteSpace: 'nowrap' }}>
                                {t(`${SS}col.sent`)}{' '}
                                <Typography component="span" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                                    {/* An em-dash, never a bare label: a row can carry EngagementAt with
                                        no SentAt, and "נשלח" followed by nothing would assert a send the
                                        server never recorded. Same fallback the deleted row used. */}
                                    {row.SentAt ? moment(row.SentAt).format(DateFormats.DATE_TIME_24) : '—'}
                                </Typography>
                                {hasEngagement ? ' ·' : ''}
                            </Typography>
                            {/* OMITTED, not printed as "לא נרשמה פעילות", when there is no engagement
                                (owner decision 2026-08-13). Absence of an open is not evidence the mail
                                was not read — email open tracking is image-load based — and the caveat
                                that says so lives on the panel OUTSIDE this drawer
                                (SendSearchPanel.tsx:552-561). Nothing else on this screen states the
                                absence per row either: SendStatusCell.tsx:97-98 drops the engagement
                                half when the state is None. */}
                            {hasEngagement && (
                                <Typography component="span" style={{ whiteSpace: 'nowrap' }}>
                                    {/* Built from the NARROWED `emailAttempt.EngagementState`, never from
                                        row.EngagementState — the same expression the headline uses. An
                                        out-of-domain value read off the raw row would print the literal
                                        key `SendSearch.engagement.bounced` here while SendStatusCell,
                                        three lines below, printed "לא מזוהה" (D10). */}
                                    {' '}{t(`${SS}engagement.${camelCaseState(emailAttempt ? emailAttempt.EngagementState : null)}`)}{' '}
                                    {row.EngagementAt && (
                                        <Typography component="span" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                                            {moment(row.EngagementAt).format(DateFormats.DATE_TIME_24)}
                                        </Typography>
                                    )}
                                </Typography>
                            )}
                        </Typography>
                    )}
                    {/* The preview action, moved up from "פרטי השליחה". The 2026-08-09 note that put it
                        beside the sent date is honoured, not reversed — the sent date moved here with it.
                        `marginInlineStart:'auto'` and not a physical margin: inline styles bypass
                        jss-rtl, so only the logical property lands on the end edge in he AND en/pl. Same
                        idiom as SendSearchPanel.tsx:500.
                        The Tooltip must keep wrapping a <span> rather than the Button: MUI v4 attaches
                        its listeners to the cloned child, a disabled <button> emits no pointer events,
                        and `PreviewUrl` is null for EVERY row until deploy script 23 runs (:33-38) — so
                        the disabled path IS the production path, and the tooltip that explains it would
                        be the one tooltip that never appears. REACT-TEST-PLAN.md:162 makes not asserting
                        on tooltips explicit policy, so no test would catch that regression. */}
                    {/* Only for a person who HAS an e-mail attempt (review 2026-09-30): offering "the
                        e-mail that was sent" to someone who got only the WhatsApp is a claim. */}
                    {hasEmailAttempt && (
                    <Tooltip title={(previewUrl ? t(`${SS}preview.button`) : t(`${SS}preview.disabled`)) as string} PopperProps={IN_DRAWER_POPPER}>
                        <span style={{ marginInlineStart: 'auto' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                disabled={!previewUrl}
                                startIcon={<MailOutline />}
                                onClick={() => setPreviewOpen(true)}
                            >
                                {t(`${SS}preview.button`)}
                            </Button>
                        </span>
                    </Tooltip>
                    )}
                </Box>
            </Card>
            )}

            {/* ── the WHATSAPP card (11-sendsearch §8) ──
                unit · template · number · sent → delivered → read · error when failed ·
                "צפייה בהודעה". Every value is the row's own; a time the server did not record is
                an em-dash, never a guess. */}
            {waHead && waAttempt && (
            <Card title={t(channelFilterKey(eSendSearchChannel.WHATSAPP))}>
                <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <Box style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <Typography component="div" style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.3, color: TONE_COLOR[waHead.tone] }}>
                            {waHead.text}
                        </Typography>
                        {/* WHY nothing went out, when nothing did — the model's decision, worded. */}
                        {waSub && waSub.kind === 'reason' && (
                            <Typography component="div" style={{ fontSize: 13.5, color: 'var(--pls-d-text-2, #5b6b7b)', marginTop: 3 }}>
                                {t(`${SS}wa.reason.${waSub.reason}`)}
                            </Typography>
                        )}
                    </Box>
                    {/* Disabled (with its reason) when no message row exists. The Tooltip wraps a
                        <span> for the same reason the e-mail button's does: a disabled button emits
                        no pointer events, so the tooltip explaining it would never appear. */}
                    <Tooltip title={(waMessageId > 0 ? t(`${SS}wa.card.view`) : t(`${SS}wa.card.viewDisabled`)) as string} PopperProps={IN_DRAWER_POPPER}>
                        <span style={{ marginInlineStart: 'auto' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                disabled={waMessageId <= 0}
                                startIcon={<Visibility />}
                                onClick={() => setWaOpen(true)}
                            >
                                {t(`${SS}wa.card.view`)}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <Box style={{ marginTop: 10 }}>
                    {!!row.WaUnitName && (
                        <Kv label={t(`${SS}wa.card.unit`)}>
                            <bdi>{row.WaUnitName}</bdi>
                        </Kv>
                    )}
                    {!!row.WaTemplateName && (
                        <Kv label={t(`${SS}wa.card.template`)}>
                            <bdi>{row.WaTemplateName}</bdi>
                        </Kv>
                    )}
                    {/* The number the WhatsApp actually used (review 2026-09-30) — never the e-mail
                        client's phone of a merged row, which can be a different, older number. */}
                    {!!waNumber && (
                        <Kv label={t(`${SS}wa.card.number`)}>
                            <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{waNumber}</span>
                        </Kv>
                    )}
                    {/* sent → delivered → read: printed only when a message exists (a 'NoPhone'
                        person has no timeline to show, and three dashes would read as three events
                        that did not happen). */}
                    {(waMessageId > 0 || row.WaSentAt != null) && (
                        <>
                            <Kv label={t(`${SS}wa.card.sent`)}>
                                <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{fmtAt(row.WaSentAt)}</span>
                            </Kv>
                            <Kv label={t(`${SS}wa.card.delivered`)}>
                                <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{fmtAt(row.WaDeliveredAt)}</span>
                            </Kv>
                            <Kv label={t(`${SS}wa.card.read`)}>
                                <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{fmtAt(row.WaReadAt)}</span>
                            </Kv>
                        </>
                    )}
                    {waSub && waSub.kind === 'error' && (
                        <Kv label={t(`${SS}wa.card.error`)}>
                            <Box style={{ color: TONE_COLOR.bad, textAlign: 'end' }}>
                                <div>{waSub.code != null ? t(`${SS}wa.errorCode`, { code: waSub.code }) : t(`${SS}wa.errorNoCode`)}</div>
                                {!!row.WaErrorText && (
                                    <div style={{ fontWeight: 400, fontSize: 13 }}><bdi>{row.WaErrorText}</bdi></div>
                                )}
                            </Box>
                        </Kv>
                    )}
                </Box>
            </Card>
            )}

            <Card title={t(`${SS}drawer.sendDetails`)}>
                <Kv label={t(`${SS}col.mailing`)}>
                    <Typography component="span" style={{ fontWeight: 700 }}>{row.CampaignName}</Typography>
                </Kv>
                <Kv label={t(`${SS}col.supervisor`)}>
                    <Typography component="span" style={{ fontWeight: 700 }}>{row.SupervisorName || '—'}</Typography>
                </Kv>
                {/* The version travels WITH the send details, never in a separate tab: "which version"
                    and "when" are one answer. Never blank — VersionBadge guarantees that.
                    When the send is ATTRIBUTABLE (see `attributed` above) the source name and the
                    recorded date join it here and the card below disappears — one answer in one place,
                    which is what the owner asked for on 2026-08-13. */}
                <Kv label={t(`${SS}version.label`)}>
                    {attributed ? (
                        // The same flex group the provenance card uses, so badge + name + date wrap
                        // instead of running off the end of the row.
                        <Box style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                            <VersionBadge
                                // ALL FIVE PROPS FROM ONE RECORD. A badge fed half from `row` and half
                                // from the provenance row could decorate one version number with an
                                // outdated flag computed for a different one.
                                VersionNumber={attributed.VersionNumber}
                                // A provenance ROW is by definition a recorded send — that is what the
                                // table is, and it is the same assertion the card below makes.
                                ProvenanceSource="Recorded"
                                VersionState={attributed.VersionState}
                                // 🔴 NOT OPTIONAL. `version.outdated` ("מיושנת") and `version.latest` are
                                // reachable ONLY through these two props (VersionBadge.tsx:101-104), and
                                // the provenance card was the ONLY agent-path caller that passed them.
                                // Suppressing that card without carrying them here would silently delete
                                // the outdated-version warning in the common case.
                                IsOutdated={attributed.IsOutdated}
                                LatestVersionNumber={attributed.LatestVersionNumber}
                                tooltipPopperProps={IN_DRAWER_POPPER}
                            />
                            {/* <bdi>, not a plain span: a Latin source name ("AgentPortfolio_Q3") would
                                otherwise drag the date to the wrong end of the line — same reason
                                SendSearchTable.tsx:167-169 wraps the source name. Falls back to the
                                existing `source.nameNotFound` string rather than rendering an empty gap.
                                No `#id` and no `source.prefix` here, deliberately: the card this replaces
                                shows neither, and `prefixMapped` would be a claim about the CURRENT
                                mapping, which a provenance-row name is not. */}
                            <bdi style={{ fontSize: 13.5, fontWeight: 400, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                                {attributed.DataSourceName || t(`${SS}source.nameNotFound`)}
                            </bdi>
                            {/* LABELLED, and the label matters: this is the provenance RECORD's timestamp,
                                not this recipient's send time (row.SentAt, in the verdict card above).
                                They can be days apart — 09/08 09:16 against 12/08 10:04 on the owner's own
                                row — so two bare dates in one drawer would be unreadable. The date is its
                                own isolated node because a Hebrew label and two Latin number runs in one
                                text node swap the clock and the date around. */}
                            <Typography component="span" style={{ fontSize: 13.5, fontWeight: 400, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                                {t(`${SS}version.recordedAt`)}{' '}
                                <Typography component="span" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                                    {moment(attributed.SentAt).format(DateFormats.DATE_TIME_24)}
                                </Typography>
                            </Typography>
                        </Box>
                    ) : (
                        <VersionBadge
                            VersionNumber={row.VersionNumber}
                            ProvenanceSource={row.ProvenanceSource}
                            VersionState={row.VersionState}
                            tooltipPopperProps={IN_DRAWER_POPPER}
                        />
                    )}
                </Kv>
                {/* The "נשלח <date>" row and the preview button that shared it BOTH moved up into the
                    verdict card on 2026-08-13 (owner request), where the sent time now sits beside the
                    engagement time under one pair of labels. The 2026-08-09 rule that the button lives
                    beside the fact it acts on is preserved, not reversed: the fact moved with it. Do not
                    re-add a second "נשלח" here — it would be the third print of one timestamp. */}
                {row.RollupValue && (
                    <Kv label={t(`${SS}roster.gap`)}>
                        {/* QA BUG-17 (X-08) — the server sends the gap cell RAW ("formatting is the UI's job",
                            DataSources_SearchSends_Stage RollupValue); the source viewer's own formatter
                            groups thousands and leaves a non-numeric value untouched. */}
                        <Typography component="span" style={{ fontWeight: 700, direction: 'ltr' }}>{formatValue(row.RollupValue, { type: eDataType.NUMBER })}</Typography>
                    </Kv>
                )}
            </Card>

            {/* ── provenance history for this campaign (GET api/SendSearch/Provenance) ──
                A campaign can legitimately have MANY provenance rows: the table has no unique
                constraint on (CampaignID, Channel) precisely because a repeat send gets its own row
                (CONTRACT §2.1). So this is a LIST, newest first as the SP returns it — collapsing it
                to "the version" would hide a second send.

                SUPPRESSED (2026-08-13) only when `attributed` is non-null — i.e. when a SINGLE
                'Recorded' row is the server's own attribution for this recipient, in which case its
                name, date and version already sit in the גרסה row above and this card would be a
                verbatim repeat. Every other shape keeps it: zero rows (this card carries the
                recorded/inferred/unverifiable sentence, which lives nowhere else), MORE than one row (a
                repeat send must stay visible), a failed fetch, still loading, and any row the server
                graded Inferred or Unverifiable (an unattributable version must not be quoted beside
                this recipient's badge). The card is deliberately NOT re-indented under the guard — the
                diff would be 47 lines of whitespace over a two-line change. */}
            {!attributed && (
            <Card title={t(`${SS}drawer.provenance`)}>
                {provenanceLoading && <CircularProgress size={18} />}
                {!provenanceLoading && !!provenanceError && (
                    // The history could not be loaded. Say THAT — never the reassuring sentence below,
                    // which is a claim about data we do not have. Reuses the screen's existing
                    // `error.loadFailed` string rather than minting a key the JSON does not carry.
                    <Typography component="div" style={{ fontSize: 13.5, color: 'var(--pls-d-danger, #B42318)', fontWeight: 700 }}>
                        {t(`${SS}error.loadFailed`)}
                    </Typography>
                )}
                {!provenanceLoading && !provenanceError && provenance.length === 0 && (
                    // An empty history is the NORMAL answer for a send that predates the provenance
                    // table — it is what makes the row 'Inferred' or 'Unverifiable' (D7). Saying so is
                    // required; an empty card would read as a failed fetch.
                    <Typography component="div" style={{ fontSize: 13.5, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                        {/* Same narrowing rule as the states, applied to the provenance domain, and
                            the same degrade VersionBadge uses: an out-of-domain (or NULL) source
                            becomes 'Unverifiable' — the CONSERVATIVE end of the ladder — because
                            `${SS}version.tooltip.` + an unknown word is a raw untranslated key on
                            screen, and degrading to 'Recorded'/'Inferred' would assert a confidence
                            the server never expressed. §4.1 defines exactly three tooltip keys. */}
                        {t(`${SS}version.tooltip.${camelCaseState(
                            isProvenanceSource(row.ProvenanceSource) ? row.ProvenanceSource : 'Unverifiable',
                        )}`)}
                    </Typography>
                )}
                {!provenanceLoading && provenance.map((p) => (
                    <Kv
                        key={p.SendProvenanceID}
                        label={moment(p.SentAt).format(DateFormats.DATE_TIME_24)}
                    >
                        <Box style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography component="span" style={{ fontSize: 13.5 }}>{p.DataSourceName}</Typography>
                            <VersionBadge
                                VersionNumber={p.VersionNumber}
                                // A provenance ROW is by definition a recorded send — that is what the
                                // table is. It is the only place 'Recorded' can be asserted from the
                                // client without inventing a claim.
                                ProvenanceSource="Recorded"
                                VersionState={p.VersionState}
                                IsOutdated={p.IsOutdated}
                                LatestVersionNumber={p.LatestVersionNumber}
                                tooltipPopperProps={IN_DRAWER_POPPER}
                            />
                        </Box>
                    </Kv>
                ))}
            </Card>
            )}

            {/* ── the values this recipient actually received (GET api/SendSearch/RowValues) ──
                Read from the LOCKED source version that was sent, not rebuilt from the template —
                see the reversal note in the header. Order is the SERVER's (the SP already sorts by
                tm.DisplayOrder, tm.TokenMapID); this must never re-sort, or the card stops matching
                the mapping screen the operator has open beside it.
                These are the RECORDED token values; the header's preview button shows the RENDERED
                mail. Two different pieces of evidence, neither derived from the other. */}
            {/* 12-fix-wave 5.12 — an e-mail row only: RowValues answers 400 for any other channel, and
                SendSearchPanel does not ask for it on a WhatsApp row.
                11-sendsearch §8: "values card only when HasEmail" — the person must have an e-mail
                attempt, not merely sit under a send that has one. */}
            {hasEmailAttempt && (
            <Card title={t(`${SS}drawer.rowValues`)}>
                {rowValuesLoading && <CircularProgress size={18} />}
                {!rowValuesLoading && (!!rowValuesError || valuesUnavailable) && (
                    // Could not load, or was never asked. Say THAT — never the no-values sentence
                    // below, which is a positive claim about data we do not have. Reuses the
                    // screen's existing `error.loadFailed` string rather than minting a key the
                    // JSON does not carry, exactly as the provenance card above does.
                    <Typography component="div" style={{ fontSize: 13.5, color: 'var(--pls-d-danger, #B42318)', fontWeight: 700 }}>
                        {/* Both keys already exist in all three locales. The permission case gets its
                            own sentence because "you may not see recipient data" and "loading failed"
                            send the operator to two different places, and only one of them is a bug. */}
                        {t(rowValuesError === 'PERMISSION_DENIED'
                            ? `${SS}error.permissionDenied`
                            : `${SS}error.loadFailed`)}
                    </Typography>
                )}
                {!rowValuesLoading && !rowValuesError && !valuesUnavailable && !hasSourceRow && (
                    // HasRow = false, or an empty list (the campaign carries no token mapping).
                    // Both mean the same thing to the operator on the phone: nothing personal was
                    // filled in for this person. NO value rows are rendered in this branch — a
                    // column of blanks would read as "we sent them empty text on purpose".
                    <Typography component="div" style={{ fontSize: 13.5, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                        {t(`${SS}drawer.noRowValues`)}
                    </Typography>
                )}
                {!rowValuesLoading && !rowValuesError && !valuesUnavailable && hasSourceRow
                    && values.map((v, i) => (
                        <Kv key={`${v.Token}-${i}`} label={v.Token}>
                            {/* An em-dash, never an empty cell: a token that genuinely resolved to
                                '' is a real fact about the send, and a blank <Box> is indistinguishable
                                from a rendering bug. `direction: ltr` because the values are IDs,
                                policy numbers and dates — RTL would reorder their digits on screen. */}
                            <Typography component="span" style={{ fontWeight: 700, direction: 'ltr' }}>
                                {(v.Value || '').trim() !== '' ? v.Value : '—'}
                            </Typography>
                        </Kv>
                    ))}
            </Card>
            )}
        </>
    );
};

export default AgentDrawer;
